import { HolderOutlined } from '@ant-design/icons';
import { ECHART_EVENT, ChartTooltip, ChartTitle, MeteorEchart, Loading } from '@meteor/frontend-core';
import { Flex, Modal, Select } from 'antd';
import React, { CSSProperties, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import ChartContent from './widgets/chart-content';
import ChartEmpty from './widgets/chart-empty';
import { OptionProps } from 'antd/es/select';
import { AppContext } from '../../contexts/AppContext';

type SelectOption = {
  default: string;
  options: OptionProps[];
};

type ChartProps = {
  chartOptions: any;
  contentList?: any;
  title?: {
    value: string | ReactNode;
    styles?: CSSProperties;
  };
  extra?: {
    value: string | ReactNode;
    styles?: CSSProperties;
  };
  titleTooltips?: string;
  height: number | string;
  tooltips?: any[];
  showSeriesLabel?: boolean;
  mouseWheelZoomLock?: boolean;
  loading?: boolean;
  isBank?: boolean;
  chartRef?: any;
  modalChartRef?: any;
  drag?: boolean;
  onReset?: Function;
  opts?: { renderer: 'canvas' | 'svg' };
  onOpen?: () => void;
  onClick?: (params) => void;
  mouseoverQuery?: string;
  onMouseover?: (params) => void;
  onMouseout?: (params) => void;
  onMousemove?: (params) => void;
  onFitView?: (params) => void;
  onHighlight?: (params) => void;
  onDownplay?: (params) => void;
  onDatazoom?: (params) => void;
  onGlobalout?: (params) => void;
  onLegendSelectChanged?: (params) => void;
  select?: SelectOption;
};

const ContentChart: React.FC<ChartProps> = (props) => {
  const {
    tooltips,
    chartOptions,
    title,
    extra,
    titleTooltips,
    contentList,
    height,
    showSeriesLabel = false,
    mouseWheelZoomLock = false,
    loading = false,
    isBank = false,
    chartRef,
    modalChartRef,
    drag = false,
    onReset,
    opts = { renderer: 'svg' },
    onOpen,
    onClick,
    onMousemove,
    onMouseover,
    onMouseout,
    onFitView,
    onHighlight,
    onDownplay,
    onDatazoom,
    onGlobalout,
    onLegendSelectChanged,
    select,
  } = props;
  const { echartTheme } = useContext(AppContext);
  const [showLabel, setShowLabel] = useState(showSeriesLabel);
  const [zoomLock, setZoomLock] = useState(mouseWheelZoomLock);

  const [option, setOption] = useState<any>({});
  const chartSelfRef: any = useRef(null);
  const modalChartSelfRef = useRef(null);
  const currentRef = chartRef || chartSelfRef;
  const modelCurrentRef = modalChartRef || modalChartSelfRef;

  const onClickRef = useRef<Function>(onClick);
  onClickRef.current = onClick;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  let modalHeight = 500;
  let modalWidth = 500;
  if (typeof window !== 'undefined') {
    modalHeight = document.body.clientHeight * 0.7;
    modalWidth = document.body.clientWidth * 0.7;
  }

  const getOptions = () => {
    if (chartOptions.series) {
      chartOptions.series.forEach((series: any) => {
        if (series) {
          series.label = series.label || {};
          if (series.label.show === undefined) {
            series.label.show = showLabel;
          }
        }
      });
    }
    if (chartOptions.dataZoom) {
      chartOptions.dataZoom.forEach((zoom: any) => {
        if (zoom.type === 'inside') {
          zoom.zoomLock = zoomLock;
        }
      });
    }
    return {
      ...chartOptions,
    };
  };

  useEffect(() => {
    const op = {
      ...getOptions(),
      echartTheme,
    };
    setOption(op);
    if (currentRef && currentRef.current) {
      const instance = currentRef.current.getEchartsInstance();
      if (instance) {
        instance.setOption(op, true);
      }
    }
    if (modelCurrentRef && modelCurrentRef.current) {
      const instance = modelCurrentRef.current.getEchartsInstance();
      if (instance) {
        instance.setOption(op, true);
      }
    }
  }, [chartOptions, showLabel, zoomLock]);

  useEffect(() => {
    setTimeout(() => {
      if (!currentRef || !currentRef.current) {
        return;
      }
      const instance = currentRef.current.getEchartsInstance();

      instance.off('legendselectchanged');
      instance.on('legendselectchanged', (event: any) => {
        if (onLegendSelectChanged) {
          onLegendSelectChanged(event);
        }
      });

      instance.off('click');
      instance.on('click', (event: any) => {
        if (event.componentType === 'series') {
          if (onClickRef.current) {
            onClickRef.current(event);
          }
        }
      });

      instance.off('mouseover');
      instance.on('mouseover', (event: any) => {
        if (onMouseover) {
          onMouseover(event);
        }
      });
      instance.off('mouseout');
      instance.on('mouseout', (event: any) => {
        if (onMouseout) {
          onMouseout(event);
        }
      });
      instance.off('mousemove');
      instance.on('mousemove', (event: any) => {
        if (onMousemove) {
          onMousemove(event);
        }
      });
      instance.off('highlight');
      instance.on('highlight', (event: any) => {
        if (onHighlight) {
          onHighlight(event);
        }
      });
      instance.off('downplay');
      instance.on('downplay', (event: any) => {
        if (onDownplay) {
          onDownplay(event);
        }
      });
      instance.off('datazoom');
      instance.on('datazoom', (event: any) => {
        if (onDatazoom) {
          onDatazoom(event);
        }
      });
      instance.off('globalout');
      instance.on('globalout', (event: any) => {
        if (onGlobalout) {
          onGlobalout(event);
        }
      });
    }, 800);
  }, [currentRef.current]);

  useEffect(() => {
    setTimeout(() => {
      if (!modelCurrentRef || !modelCurrentRef.current) {
        return;
      }
      const instance = modelCurrentRef.current.getEchartsInstance();

      instance.off('legendselectchanged');
      instance.on('legendselectchanged', (event: any) => {
        if (onLegendSelectChanged) {
          onLegendSelectChanged(event);
        }
      });

      instance.off('click');
      instance.on('click', (event: any) => {
        if (event.componentType === 'series') {
          if (onClickRef.current) {
            onClickRef.current(event);
          }
        }
      });

      instance.off('mouseover');
      instance.on('mouseover', (event: any) => {
        if (onMouseover) {
          onMouseover(event);
        }
      });
      instance.off('mouseout');
      instance.on('mouseout', (event: any) => {
        if (onMouseout) {
          onMouseout(event);
        }
      });
      instance.off('mousemove');
      instance.on('mousemove', (event: any) => {
        if (onMousemove) {
          onMousemove(event);
        }
      });
      instance.off('highlight');
      instance.on('highlight', (event: any) => {
        if (onHighlight) {
          onHighlight(event);
        }
      });
      instance.off('downplay');
      instance.on('downplay', (event: any) => {
        if (onDownplay) {
          onDownplay(event);
        }
      });
      instance.off('datazoom');
      instance.on('datazoom', (event: any) => {
        if (onDatazoom) {
          onDatazoom(event);
        }
      });
      instance.off('globalout');
      instance.on('globalout', (event: any) => {
        if (onGlobalout) {
          onGlobalout(event);
        }
      });
    }, 800);
  }, [modelCurrentRef.current, isModalOpen]);

  useEffect(() => {
    const instance = modelCurrentRef.current?.getEchartsInstance();
    if (instance) {
      instance.resize();
    }
    if (isModalOpen && onOpen) {
      onOpen();
    }
  }, [isModalOpen]);

  const reset = () => {
    if (onReset) {
      onReset();
    }
  };

  const onTooltipClick = (type: string, show: boolean = false) => {
    if (type === 'zoomIn') {
      showModal();
    }
    if (type === 'reset') {
      reset();
    }
    if (type === 'label') {
      setShowLabel(show);
    }
    if (type === 'zoomLock') {
      setZoomLock(show);
    }
    if (type === 'fitView') {
      const actionEvent = {
        eventType: ECHART_EVENT.CHART_FIT_VIEW,
        chartTitle: title,
      };
      if (onFitView) {
        onFitView(actionEvent);
      }
    }
  };

  return (
    <>
      <div className="summary-kpi-card">
        {isBank ? (
          <></>
        ) : (
          <ChartTooltip
            items={tooltips || ['zoomIn', 'reset']}
            showSeriesLabel={showSeriesLabel}
            mouseWheelZoomLock={mouseWheelZoomLock}
            onClick={onTooltipClick}
          />
        )}
        <Flex justify="space-between">
          <div style={{ overflow: 'hidden' }}>
            {drag && <HolderOutlined style={{ height: 24, marginTop: 2, marginRight: 6, cursor: 'move' }} />}
            {title?.value && <ChartTitle title={title} titleTooltips={titleTooltips} />}
          </div>
          {extra && (
            <div style={{ display: 'flex', paddingBottom: 6, flex: 1, whiteSpace: 'nowrap', justifyContent: 'flex-end', ...extra.styles }}>
              {extra.value}
            </div>
          )}
        </Flex>
        {select && <Select defaultValue={select.default} options={select.options} />}
        {!loading ? (
          <>
            {contentList && <ChartContent contentList={contentList} />}
            {isBank ? <ChartEmpty /> : <MeteorEchart opts={opts} chartRef={currentRef} option={option} style={{ height: height || 160 }} />}
          </>
        ) : (
          <Loading fixed={false} />
        )}
      </div>

      <Modal
        className="aipcmc"
        title={title?.value || null}
        open={isModalOpen}
        onCancel={handleCancel}
        width={modalWidth + 100}
        footer={null}
      >
        <div className="summary-kpi-modal">
          <div className="summary-kpi-card">
            <ChartTooltip
              items={
                tooltips
                  ? tooltips.filter((item) => {
                      return item !== 'zoomIn';
                    })
                  : ['reset']
              }
              showSeriesLabel={showSeriesLabel}
              onClick={onTooltipClick}
            />
            {contentList && <ChartContent contentList={contentList} />}
            <MeteorEchart opts={opts} chartRef={modelCurrentRef} option={option} style={{ height: modalHeight }} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ContentChart;
